import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { useProductData } from '../data'
import { loadListingTimes, type ListingTimesData } from '../listingTimes'
import { JD_STORE_ID, productMasterCostDefaults, productMasterRecordMap } from '../productMaster'
import { internalProductRecordsByCode, summarizeProductProfit } from '../productProfit'
import type { DailyMetrics, ProductRecord } from '../types'
import { integer, metricsAcrossDates, money, percent } from '../utils'

const WINDOW_OPTIONS = [7, 30, 60, 90]

type NewProductSortKey = 'createdAt' | 'gmv' | 'profitWithRebate' | 'soldItemCount'

interface NewProductRow {
  jdSkuId: string
  createdAt: string
  createdDate: string
  status: string
  listedDays: number
  partialData: boolean
  product: ProductRecord | undefined
  metrics: DailyMetrics | null
  coveredDays: number
  profitWithRebate: number | null
  profitContext: string
  dailyAverageGmv: number | null
}

export function NewProductsPage() {
  const { data, selectedDate, productIndex, segmentIndex, productMaster, internalProductInfo } = useProductData()
  const [listing, setListing] = useState<ListingTimesData | null | undefined>(undefined)
  const [windowDays, setWindowDays] = useState(30)
  const [sortKey, setSortKey] = useState<NewProductSortKey>('createdAt')

  useEffect(() => {
    let cancelled = false
    loadListingTimes().then((value) => { if (!cancelled) setListing(value) })
    return () => { cancelled = true }
  }, [])

  const productMasterRecords = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const companyRecordsByCode = useMemo(
    () => internalProductRecordsByCode(internalProductInfo?.records || []),
    [internalProductInfo],
  )

  const anchorDate = data?.meta.dates[data.meta.dates.length - 1] || ''
  const earliestDate = data?.meta.dates[0] || ''

  const rows = useMemo<NewProductRow[]>(() => {
    if (!data || !listing || !anchorDate) return []
    const anchorTime = new Date(`${anchorDate}T00:00:00`).getTime()
    return listing.records.flatMap((record) => {
      const createdDate = record.createdAt.slice(0, 10)
      const createdTime = new Date(`${createdDate}T00:00:00`).getTime()
      if (!Number.isFinite(createdTime) || createdTime > anchorTime) return []
      const listedDays = Math.floor((anchorTime - createdTime) / 86400000) + 1
      if (listedDays > windowDays) return []
      const product = productIndex.get(`sku:${record.jdSkuId}`)
      const datesSince = data.meta.dates.filter((date) => date >= createdDate)
      const metrics = product && datesSince.length ? metricsAcrossDates(product, datesSince) : null
      const profitSummary = product && datesSince.length
        ? summarizeProductProfit(
          [product],
          datesSince,
          selectedDate,
          productMasterRecords,
          costDefaults,
          companyRecordsByCode,
          segmentIndex,
        )
        : null
      return [{
        jdSkuId: record.jdSkuId,
        createdAt: record.createdAt,
        createdDate,
        status: record.status,
        listedDays,
        partialData: createdDate < earliestDate,
        product,
        metrics,
        coveredDays: datesSince.length,
        profitWithRebate: profitSummary?.profitWithRebate ?? null,
        profitContext: profitSummary?.context || '暂无商智销售记录',
        dailyAverageGmv: metrics && datesSince.length ? metrics.gmv / datesSince.length : null,
      }]
    })
  }, [anchorDate, companyRecordsByCode, costDefaults, data, earliestDate, listing, productIndex, productMasterRecords, segmentIndex, selectedDate, windowDays])

  const sortedRows = useMemo(() => {
    const next = [...rows]
    next.sort((a, b) => {
      if (sortKey === 'createdAt') return b.createdAt.localeCompare(a.createdAt)
      if (sortKey === 'gmv') return (b.metrics?.gmv || 0) - (a.metrics?.gmv || 0)
      if (sortKey === 'soldItemCount') return (b.metrics?.soldItemCount || 0) - (a.metrics?.soldItemCount || 0)
      return (b.profitWithRebate ?? -Infinity) - (a.profitWithRebate ?? -Infinity)
    })
    return next
  }, [rows, sortKey])

  const summary = useMemo(() => {
    const active = rows.filter((row) => (row.metrics?.gmv || 0) > 0)
    const covered = rows.filter((row) => row.profitWithRebate !== null)
    return {
      total: rows.length,
      activeCount: active.length,
      totalGmv: active.reduce((sum, row) => sum + (row.metrics?.gmv || 0), 0),
      totalItems: active.reduce((sum, row) => sum + (row.metrics?.soldItemCount || 0), 0),
      profitWithRebate: covered.length
        ? covered.reduce((sum, row) => sum + (row.profitWithRebate || 0), 0)
        : null,
      coveredCount: covered.length,
      zeroCount: rows.length - active.length,
    }
  }, [rows])

  if (!data) return null

  return (
    <div className="detail-page new-products-page">
      <div className="section-heading-row period-heading">
        <div>
          <h1>新品分析</h1>
          <p>
            上架时间取商家商品清单“创建时间”（快照 {listing?.latestExportDate || '加载中'}）
            · 上架天数按最新业务日期 {anchorDate} 计算 · 销售数据覆盖 {earliestDate} 起
          </p>
        </div>
        <div className="metric-switch" role="group" aria-label="新品窗口">
          {WINDOW_OPTIONS.map((option) => (
            <button
              type="button"
              key={option}
              className={option === windowDays ? 'is-active' : ''}
              aria-pressed={option === windowDays}
              onClick={() => setWindowDays(option)}
            >
              {option}天内
            </button>
          ))}
        </div>
      </div>

      <MetricStrip items={[
        { label: '窗口内新品', value: integer(summary.total), context: `创建时间距今 ≤ ${windowDays} 天` },
        { label: '动销新品', value: integer(summary.activeCount), context: '上架以来有成交' },
        { label: '累计成交金额', value: money(summary.totalGmv), context: `合计 ${integer(summary.totalItems)} 件` },
        {
          label: '含返点利润',
          value: summary.profitWithRebate === null ? '—' : money(summary.profitWithRebate),
          context: `成本已覆盖 ${integer(summary.coveredCount)}/${integer(summary.total)} 个新品`,
        },
        { label: '未动销新品', value: integer(summary.zeroCount), context: '上架以来无成交' },
      ]} />

      <section className="detail-section">
        <div className="section-heading-row">
          <div>
            <h2>新品清单</h2>
            <p>共 {integer(sortedRows.length)} 个 · 点击商品进入SKU详情</p>
          </div>
          <label className="new-product-sort">
            <span>排序</span>
            <select value={sortKey} onChange={(event) => setSortKey(event.target.value as NewProductSortKey)}>
              <option value="createdAt">上架时间 新→旧</option>
              <option value="gmv">累计成交 高→低</option>
              <option value="soldItemCount">成交件数 多→少</option>
              <option value="profitWithRebate">含返点利润 高→低</option>
            </select>
          </label>
        </div>
        {listing === undefined && <p className="formula-empty">正在加载上架时间数据…</p>}
        {listing === null && <p className="formula-empty profit-missing">上架时间数据加载失败，请确认已运行 scripts/import_product_listing_times.py。</p>}
        {listing && !sortedRows.length && <p className="formula-empty">当前窗口内没有新品；可切换更长时间窗口。</p>}
        {!!sortedRows.length && (
          <div className="table-responsive">
            <table className="history-table new-products-table">
              <thead>
                <tr>
                  <th>商品</th>
                  <th>上架日期</th>
                  <th>上架天数</th>
                  <th>累计成交金额</th>
                  <th>成交件数</th>
                  <th>访客</th>
                  <th>转化率</th>
                  <th>含返点利润</th>
                  <th>日均成交</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.jdSkuId}>
                    <td className="new-product-cell-wrap">
                      <div className="new-product-cell">
                        {row.product?.image
                          ? <img src={row.product.image} alt="" />
                          : <span className="new-product-image-fallback">无图</span>}
                        <span>
                          <Link to={`/product/sku/${row.jdSkuId}`} title={row.product?.name || `SKU ${row.jdSkuId}`}>
                            {row.product?.name || `SKU ${row.jdSkuId}`}
                          </Link>
                          <small>
                            {row.jdSkuId}
                            {row.product?.merchantSku ? ` · 商家SKU ${row.product.merchantSku}` : ''}
                            {!row.product && ' · 近30天无商智记录'}
                          </small>
                        </span>
                      </div>
                    </td>
                    <td>{row.createdDate}</td>
                    <td>
                      第 {integer(row.listedDays)} 天
                      {row.partialData && <small className="partial-tag" title={`上架早于数据窗口，累计值仅含 ${earliestDate} 起的数据`}>仅近30天</small>}
                    </td>
                    <td>{row.metrics ? money(row.metrics.gmv) : '—'}</td>
                    <td>{row.metrics ? integer(row.metrics.soldItemCount) : '—'}</td>
                    <td>{row.metrics ? integer(row.metrics.productVisitors) : '—'}</td>
                    <td>{row.metrics ? percent(row.metrics.conversionRate) : '—'}</td>
                    <td
                      className={row.profitWithRebate === null ? 'profit-missing' : row.profitWithRebate >= 0 ? 'profit-positive' : 'profit-negative'}
                      title={row.profitContext}
                    >
                      {row.profitWithRebate === null ? '—' : money(row.profitWithRebate)}
                    </td>
                    <td>{row.dailyAverageGmv === null ? '—' : money(row.dailyAverageGmv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="methodology-note">
        “创建时间”为京东系统SKU创建时间，作为上架时间的近似口径；商品清单快照更新后需重新运行导入脚本。
        累计指标按商智分天数据自上架日累加（受滚动30天数据窗口限制，早于窗口的新品标注“仅近30天”）；
        含返点利润使用与全站一致的成本口径，产品成本缺失的新品不按0计算。转化率＝累计成交客户÷累计访客。
      </p>
    </div>
  )
}
